// Cart Service
// Handles storage and retrieval of cart items, favorites, likes, and shares

export interface CartItem {
  id: string;
  type: 'accommodation' | 'activity' | 'package';
  itemId: string;
  itemName: string;
  itemPrice: number;
  quantity?: number; // Optional: defaults to 1 if not provided
  addedAt: string;
  isLiked: boolean;
  isFavorite: boolean;
  isShared: boolean;
  /** For VUM-included activities: number of units covered for free by the promo */
  vumIncludedQty?: number;
  /** For VUM-included activities: real per-person price charged for units above vumIncludedQty */
  vumLinkedActivityPrice?: number;
  /** When false, taxe d'hébergement ($3.50/night) is not applied to this accommodation line in checkout */
  taxeHebergementEnabled?: boolean;
}

const CART_STORAGE_KEY = 'camping_cart_items';

// Get all cart items from localStorage
export const getCartItems = (): CartItem[] => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    if (!Array.isArray(parsed)) return [];
    // Backward-compat: older cart items may not have quantity
    return parsed.map((item: any) => ({
      ...item,
      quantity: typeof item?.quantity === 'number' && item.quantity > 0 ? item.quantity : 1
    })) as CartItem[];
  } catch (error) {
    console.error('Error reading cart items from storage:', error);
    return [];
  }
};

// Save cart items to localStorage
export const saveCartItems = (items: CartItem[]): void => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('cartUpdated', { detail: items }));
  } catch (error) {
    console.error('Error saving cart items to storage:', error);
  }
};

// Add item to cart
export const addToCart = (item: Omit<CartItem, 'id' | 'addedAt' | 'isLiked' | 'isFavorite' | 'isShared'>): CartItem => {
  const items = getCartItems();
  
  // Check if item already exists in cart
  const existingItem = items.find(i => i.itemId === item.itemId && i.type === item.type);
  if (existingItem) {
    return existingItem; // Return existing item if already in cart
  }
  
  const newItem: CartItem = {
    id: `cart-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    ...item,
    quantity: item.quantity && item.quantity > 0 ? item.quantity : 1,
    addedAt: new Date().toISOString(),
    isLiked: false,
    isFavorite: false,
    isShared: false
  };
  
  items.push(newItem);
  saveCartItems(items);
  
  return newItem;
};

// Remove item from cart
export const removeFromCart = (itemId: string): boolean => {
  const items = getCartItems();
  const filtered = items.filter(i => i.id !== itemId);
  
  if (filtered.length === items.length) return false;
  
  saveCartItems(filtered);
  return true;
};

// If item has neither like nor favorite, remove it from cart (UX: unselecting both removes from cart)
function removeFromCartIfNeitherLikeNorFavorite(items: CartItem[], cartItemId: string): CartItem[] {
  const item = items.find(i => i.id === cartItemId);
  if (!item || item.isLiked || item.isFavorite) return items;
  return items.filter(i => i.id !== cartItemId);
}

// Toggle like status; remove from cart if both like and favorite become false
export const toggleLike = (itemId: string): boolean => {
  const items = getCartItems();
  const item = items.find(i => i.id === itemId);
  
  if (!item) return false;
  
  item.isLiked = !item.isLiked;
  const next = removeFromCartIfNeitherLikeNorFavorite(items, itemId);
  saveCartItems(next);
  return item.isLiked;
};

// Toggle favorite status; remove from cart if both like and favorite become false
export const toggleFavorite = (itemId: string): boolean => {
  const items = getCartItems();
  const item = items.find(i => i.id === itemId);
  
  if (!item) return false;
  
  item.isFavorite = !item.isFavorite;
  const next = removeFromCartIfNeitherLikeNorFavorite(items, itemId);
  saveCartItems(next);
  return item.isFavorite;
};

// Toggle share status
export const toggleShare = (itemId: string): boolean => {
  const items = getCartItems();
  const item = items.find(i => i.id === itemId);
  
  if (!item) return false;
  
  item.isShared = !item.isShared;
  saveCartItems(items);
  return item.isShared;
};

// Get item by ID
export const getCartItem = (itemId: string): CartItem | null => {
  const items = getCartItems();
  return items.find(i => i.id === itemId) || null;
};

// Check if item is in cart
export const isInCart = (type: 'accommodation' | 'activity' | 'package', itemId: string): boolean => {
  const items = getCartItems();
  return items.some(i => i.itemId === itemId && i.type === type);
};

// Get cart item count
export const getCartItemCount = (): number => {
  return getCartItems().length;
};

// Clear all cart items
export const clearCart = (): void => {
  saveCartItems([]);
};

// Update cart item details
/** Prefix used for VUM-included activity items so they can be bulk-removed. */
export const VUM_ACTIVITY_ITEM_PREFIX = 'vum-included-';

/** Remove all VUM-included activity items from the cart (called when promo is cleared). */
export const removeVumIncludedActivities = (): void => {
  const items = getCartItems();
  const filtered = items.filter((i) => !i.itemId.startsWith(VUM_ACTIVITY_ITEM_PREFIX));
  if (filtered.length !== items.length) {
    saveCartItems(filtered);
  }
};

// Update cart item details (customer-editable fields only)
export const updateCartItem = (itemId: string, updates: Partial<Pick<CartItem, 'quantity'>>): boolean => {
  const items = getCartItems();
  const itemIndex = items.findIndex(i => i.id === itemId);
  
  if (itemIndex === -1) return false;

  // Only allow quantity updates from the cart UI
  const nextQuantity = typeof updates.quantity === 'number' ? Math.max(1, Math.floor(updates.quantity)) : items[itemIndex].quantity;
  items[itemIndex] = { ...items[itemIndex], quantity: nextQuantity };
  
  saveCartItems(items);
  return true;
};


